import { motion } from 'framer-motion';
import {
  ArrowRight,
  Briefcase,
  Clock,
  Globe,
  MapPin,
  Search,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MarketingPageFooter from '../components/marketing/MarketingPageFooter';
import MarketingLandingNav from '../components/marketing/MarketingLandingNav';
import MarketingAgentBar, { MARKETING_AGENT_STICKY_ZONE_ID } from '../components/marketing/MarketingAgentBar';
import { supabase } from '../lib/supabase';
import { SEO } from '../shared/components/SEO';
import { buildCareersListingJsonLd } from '../utils/careerJobPostingSchema';

interface CareerPost {
  id: string;
  slug: string;
  title: string;
  department: string | null;
  location: string | null;
  job_type: string | null;
  experience_level: string | null;
  salary_range: string | null;
  hero_intro: string | null;
  hero_headline: string | null;
  published_at: string | null;
  closes_at: string | null;
  application_url: string | null;
}

interface CareersPageProps {
  onNavigate?: (view: string) => void;
}

const ALL_DEPARTMENTS = 'All';

const CareersPage: React.FC<CareersPageProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState(ALL_DEPARTMENTS);
  const [posts, setPosts] = useState<CareerPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('career_posts')
        .select('id,slug,title,department,location,job_type,experience_level,salary_range,hero_intro,hero_headline,published_at,closes_at,application_url')
        .eq('status', 'published')
        .order('published_at', { ascending: false });
      if (err) { setError(err.message); }
      else { setPosts((data as CareerPost[]) || []); }
      setLoading(false);
    })();
  }, []);

  const careersJsonLd = useMemo(
    () => (posts.length > 0 ? buildCareersListingJsonLd(posts) : undefined),
    [posts],
  );

  // Distinct departments for filter pills
  const departments = [ALL_DEPARTMENTS, ...Array.from(new Set(posts.map(p => p.department).filter(Boolean) as string[]))];

  const filtered = posts.filter(p => {
    const matchDept = deptFilter === ALL_DEPARTMENTS || p.department === deptFilter;
    const matchSearch = !search.trim() ||
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      (p.department || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.location || '').toLowerCase().includes(search.toLowerCase());
    return matchDept && matchSearch;
  });

  return (
    <div className="careers-marketing-page transition-colors duration-300">
      <SEO
        title={t('views.careers.pageTitle')}
        description={t('views.careers.metaDescription')}
        canonical="https://www.gograbjob.com/careers"
        jsonLd={careersJsonLd}
      />

      <MarketingLandingNav onNavigate={onNavigate} activeView="careers" variant="marketing" />

      <div
        id={MARKETING_AGENT_STICKY_ZONE_ID}
        className="landing-agent-sticky-zone marketing-agent-sticky-zone--below-nav"
      >
        <MarketingAgentBar onNavigate={onNavigate} />

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section className="careers-marketing-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="home-problem-section__eyebrow">{t('views.careers.eyebrow')}</p>
            <h1 className="home-problem-section__title">
              {t('views.careers.heroTitle')}<br className="hidden sm:block" />
              <span className="home-problem-section__title-accent"> {t('views.careers.heroTitleAccent')}</span>
            </h1>
            <p className="home-problem-section__subtitle">
              {t('views.careers.heroSubtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-stone-600 dark:text-stone-400 mt-6">
              <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> {t('views.careers.remoteFirst')}</span>
              <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> {t('views.careers.openRoles', { count: posts.length })}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {t('views.careers.indiaBased')}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Listings ─────────────────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" key={i18n.language}>

        {/* Search + dept filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('views.careers.searchPlaceholder')}
              className="careers-marketing-input pl-9"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {departments.map((d) => (
              <button
                key={d}
                onClick={() => setDeptFilter(d)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  deptFilter === d
                    ? 'bg-[#5F8568] text-white border-transparent'
                    : 'border-stone-200 dark:border-stone-600 text-stone-600 dark:text-stone-400 hover:border-[#15803d] hover:text-[#15803d]'
                }`}
              >
                {d === ALL_DEPARTMENTS ? t('views.careers.filterAll') : d}
              </button>
            ))}
          </div>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-5 animate-pulse h-48" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16 text-red-500">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('views.careers.noOpeningsTitle')}</h2>
            <p className="text-gray-500 dark:text-gray-500 text-sm">
              {posts.length === 0
                ? t('views.careers.noRolesEmpty')
                : t('views.careers.noRolesFiltered')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((post, i) => (
              <motion.a
                key={post.id}
                href={`/careers/${post.slug}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="home-problem-section__mesh careers-listing-mesh group flex flex-col overflow-hidden cursor-pointer transition-all duration-200"
              >
                <div className="hiw-mesh-layer" aria-hidden />
                <div className="home-problem-section__mesh-inner flex flex-col flex-1 !p-0">
                <div className="p-5 flex flex-col flex-1">
                  {/* Department badge */}
                  {post.department && (
                    <span className="careers-dept-pill self-start mb-3">
                      {post.department}
                    </span>
                  )}

                  <h2 className="text-base font-extrabold text-stone-900 dark:text-stone-50 mb-1.5 group-hover:text-[#15803d] transition-colors line-clamp-2">
                    {post.title}
                  </h2>

                  {post.hero_intro && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3 flex-1">{post.hero_intro}</p>
                  )}

                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-500 mt-auto">
                    {post.location && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{post.location}</span>
                    )}
                    {post.job_type && (
                      <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{post.job_type}</span>
                    )}
                    {post.experience_level && (
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.experience_level}</span>
                    )}
                  </div>

                  {post.salary_range && (
                    <p className="text-xs text-purple-600 dark:text-purple-400 font-semibold mt-2">{post.salary_range}</p>
                  )}
                </div>

                {/* Footer bar */}
                <div className="px-5 py-3 border-t border-stone-200/80 dark:border-stone-600 flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {post.closes_at
                      ? t('views.careers.closesOn', {
                          date: new Date(post.closes_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' }),
                        })
                      : post.published_at
                        ? new Date(post.published_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })
                        : ''}
                  </span>
                  <span className="flex items-center gap-1 text-xs font-semibold text-[#15803d] dark:text-[#86efac] group-hover:gap-2 transition-all">
                    {t('views.careers.apply')} <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
                </div>
              </motion.a>
            ))}
          </div>
        )}

        {/* Nothing open — encourage follow */}
        {!loading && !error && posts.length === 0 && (
          <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-800/30 text-center max-w-xl mx-auto">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{t('views.careers.stayInLoopTitle')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('views.careers.stayInLoopBody')}</p>
            <a href="https://www.linkedin.com/company/gograbjobofficial" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700 transition-colors">
              {t('views.careers.followLinkedIn')} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}
      </main>
      </div>

      <MarketingPageFooter onNavigate={onNavigate as any} theme="careers" />
    </div>
  );
};

export default CareersPage;
